import { ReactNode, useMemo } from 'react';
import cn from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import { ChevronRight } from 'lucide-react';
import { useThemeContext } from '../../../utils/providers/theme-context';

type NavigationCardProps = {
  title: string;
  description?: string;
  details?: string[];
  icon?: ReactNode;
  onClick: () => void;
  className?: string;
};

/**
 * NavigationCard — a clickable card that navigates to a section
 * - Themed with hover effects
 * - Displays title, optional description, and icon
 * - Arrow indicator for navigation
 */
export default function NavigationCard({
  title,
  description,
  details,
  icon,
  onClick,
  className
}: NavigationCardProps) {
  const { getColour } = useThemeContext();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: getColour('secondLayer'),
          borderColor: getColour('border'),
          color: getColour('normalText'),
          boxShadow:
            '0 4px 12px 2px rgba(0,0,0,0.10), 0 2px 4px rgba(0,0,0,0.06)',
          ':hover': {
            borderColor: getColour('borderPrimary'),
            backgroundColor: getColour('thirdLayer'),
            boxShadow:
              '0 6px 16px 3px rgba(0,0,0,0.12), 0 3px 6px 1px rgba(0,0,0,0.08)'
          }
        },
        iconBg: {
          backgroundColor: getColour('thirdLayer'),
          borderColor: getColour('border')
        },
        title: {
          color: getColour('normalText')
        },
        description: {
          color: getColour('inactiveText')
        },
        detail: {
          color: getColour('inactiveText')
        },
        arrow: {
          color: getColour('inactiveText')
        }
      }),
    [getColour]
  );

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full rounded-xl border p-4 transition-all cursor-pointer text-left h-full overflow-hidden',
        css(styles.card),
        className
      )}
    >
      <div className="flex flex-col h-full min-w-0">
        {/* Header with icon and title */}
        <div className="flex items-start justify-between gap-3 mb-2 min-w-0">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            {icon && (
              <div
                className={cn(
                  'p-2 rounded-xl border shrink-0',
                  css(styles.iconBg)
                )}
              >
                {icon}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3
                className={cn(
                  'text-sm font-semibold mb-1 break-words',
                  css(styles.title)
                )}
              >
                {title}
              </h3>
              {description && (
                <p
                  className={cn(
                    'text-xs leading-normal break-words',
                    css(styles.description)
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
          <ChevronRight
            size={18}
            className={cn('shrink-0 mt-1', css(styles.arrow))}
          />
        </div>

        {/* Details list */}
        {details && details.length > 0 && (
          <div
            className="mt-auto pt-2 border-t min-w-0"
            style={{ borderColor: getColour('border') }}
          >
            <ul className="space-y-1">
              {details.map((detail, index) => (
                <li
                  key={index}
                  className={cn(
                    'text-xs flex items-start gap-2 min-w-0',
                    css(styles.detail)
                  )}
                >
                  <span
                    className="w-1 h-1 rounded-full shrink-0 mt-1.5"
                    style={{ backgroundColor: getColour('inactiveText') }}
                  />
                  <span className="break-words flex-1 min-w-0">{detail}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </button>
  );
}
